// Texto 27 (B8): RELATÓRIO DO MÊS em PDF, no padrão do Relatório de estudos (escolher o que incluir, histórico de PDFs).
//   · Do escritório: reuniões, tarefas feitas e pendentes, documentos internos enviados, documentos trocados com os
//     clientes (enviados e recebidos) e clientes com pendência.
//   · De um cliente: documentos da competência, pedidos, guias pagas e vencidas e honorários.
// Nunca leva CPF/CNPJ, anotações internas nem conversas (REGRAS 8.2).
import { useState } from 'react';
import { Alert, Pressable, ScrollView, Text, View } from 'react-native';
import type { Reg } from '../../store/types';
import { useDoc } from '../../store/store';
import { nomeMes, today, ymd } from '../../lib/date';
import { N_, t } from '../../lib/i18n';
import { dinheiro, moedaPadrao } from '../../lib/moeda';
import { gerarECompartilharPdf, type BlocoPdf } from '../../lib/pdf';
import { marcarAbaAtual } from '../../lib/relatorios';
import { atrasada, falta, parcelasDe, quitada, recebsDe } from '../../lib/recebimentos';
import { semBloqueio } from '../../auth/AuthContext';
import type { MeuGrupo } from '../../lib/grupos';
import { docsDe, reunioesDe, tarefasDe } from '../../lib/escritorio';
import { Btn, Chip, Sheet } from '../ui';
import { RelatoriosGerados } from '../RelatoriosGerados';
import { C } from '../theme';
import { pastaDe, situacaoPasta } from './ClientesEsc';

const PARTES_ESC = [['reunioes', N_('Reuniões do mês')], ['tarefas', N_('Tarefas feitas e pendentes')], ['internos', N_('Documentos internos enviados')],
  ['trocados', N_('Documentos enviados e recebidos dos clientes')], ['pendencias', N_('Clientes com pendência')]] as const;
const PARTES_CLI = [['docs', N_('Documentos da competência')], ['pedidos', N_('Pedidos de documentos')], ['guias', N_('Guias pagas e vencidas')],
  ['honorarios', N_('Honorários')]] as const;
type ParteE = typeof PARTES_ESC[number][0]; type ParteC = typeof PARTES_CLI[number][0];

/** "2026-10" dos últimos 6 meses (este mês primeiro). */
const meses = () => { const d = new Date(); return Array.from({ length: 6 }, (_, i) => { const x = new Date(d.getFullYear(), d.getMonth() - i, 1); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`; }); };
const rotuloMes = (m: string) => `${nomeMes(Number(m.slice(5)) - 1)} / ${m.slice(0, 4)}`;
const comp = (m: string) => `${m.slice(5)}/${m.slice(0, 4)}`;   // "10/2026"
const br = (d: unknown) => { const s = String(d ?? ''); return /^\d{4}-\d{2}-\d{2}/.test(s) ? `${s.slice(8, 10)}/${s.slice(5, 7)}` : ''; };
const doMes = (d: unknown, m: string) => String(d ?? '').slice(0, 7) === m;
const local = (iso: string) => ymd(new Date(iso));
const ehDoCliente = (r: Reg) => r.c.origem === 'Enviado pelo cliente';
const tituloDoc = (d: Reg) => String(d.c.titulo || d.c.categoria || t('Documento'));

export function RelatorioMes({ visible, onClose, g, cor }: { visible: boolean; onClose: () => void; g?: MeuGrupo; cor: string }) {
  const base = (useDoc('clientes')?.regs ?? []).filter(r => r.col === 'cliente').sort((a, b) => String(a.c.nome ?? '').localeCompare(String(b.c.nome ?? '')));
  const portal = useDoc('portal')?.regs ?? [];
  const [mes, setMes] = useState(meses()[0]);
  const [quem, setQuem] = useState<string | null>(null);   // null = o escritório
  const [selE, setSelE] = useState<ParteE[]>(PARTES_ESC.map(p => p[0]));
  const [selC, setSelC] = useState<ParteC[]>(PARTES_CLI.map(p => p[0]));
  const [gerando, setGerando] = useState(false);
  const moeda = moedaPadrao();
  const cli = quem ? base.find(b => b.id === quem) ?? null : null;

  async function doEscritorio(): Promise<BlocoPdf[]> {
    const b: BlocoPdf[] = [{ t: 'titulo', texto: t('Relatório do mês · {m}', { m: rotuloMes(mes) }) }, { t: 'par', centro: true, texto: g?.nome ?? t('Escritório & Clientes') }];
    const tem = (p: ParteE) => selE.includes(p);
    if (g && tem('reunioes')) {
      const rs = (await reunioesDe(g.id).catch(() => [])).filter(r => doMes(r.data, mes)).sort((a, c) => (a.data + a.hora).localeCompare(c.data + c.hora));
      b.push({ t: 'sub', texto: t('Reuniões ({n})', { n: rs.length }) });
      if (rs.length) b.push({ t: 'tabela', colunas: [t('Data'), t('Hora'), t('Assunto'), t('Duração')], larguras: [0.14, 0.12, 0.54, 0.2],
        linhas: rs.map(r => [br(r.data), r.hora, r.titulo, t('{n} min', { n: r.duracao_real || r.duracao })]) });
    }
    if (g && tem('tarefas')) {
      const ts = await tarefasDe(g.id).catch(() => []);
      const feitas = ts.filter(x => x.situacao === 'feito' && x.feita_em && doMes(local(x.feita_em), mes));
      const pend = ts.filter(x => x.situacao !== 'feito');
      b.push({ t: 'sub', texto: t('Tarefas: {f} feitas no mês · {p} pendentes', { f: feitas.length, p: pend.length }) });
      if (feitas.length) b.push({ t: 'par', negrito: true, texto: t('Feitas') }, { t: 'tabela', colunas: [t('Tarefa'), t('Cliente'), t('Quem fez'), t('Feita em')], larguras: [0.42, 0.22, 0.22, 0.14],
        linhas: feitas.map(x => [x.titulo, x.cliente, x.responsavel_nome, br(local(x.feita_em!))]) });
      if (pend.length) b.push({ t: 'par', negrito: true, texto: t('Pendentes') }, { t: 'tabela', colunas: [t('Tarefa'), t('Cliente'), t('Responsável'), t('Prazo')], larguras: [0.42, 0.22, 0.22, 0.14],
        linhas: pend.map(x => [x.titulo, x.cliente, x.responsavel_nome || t('sem responsável'), x.prazo ? br(x.prazo) : '']) });
    }
    if (g && tem('internos')) {
      const ds = (await docsDe(g.id).catch(() => [])).filter(d => d.tipo !== 'gravacao' && doMes(local(d.criado_em), mes));
      b.push({ t: 'sub', texto: t('Documentos internos enviados ({n})', { n: ds.length }) });
      if (ds.length) b.push({ t: 'tabela', colunas: [t('Documento'), t('Pasta'), t('Quem enviou'), t('Data')], larguras: [0.46, 0.18, 0.22, 0.14],
        linhas: ds.map(d => [d.nome, d.pasta ?? '', d.enviado_nome, br(local(d.criado_em))]) });
    }
    if (tem('trocados')) {
      const linhas = base.map(c => {
        const p = pastaDe(c, portal); if (!p) return null;
        const ds = portal.filter(r => r.col === 'documento' && r.pai === p.id && doMes(r.c.data || r.criado, mes));
        const rec = ds.filter(ehDoCliente).length, env = ds.length - rec;
        return ds.length ? [String(c.c.nome ?? ''), String(env), String(rec)] : null;
      }).filter(Boolean) as string[][];
      b.push({ t: 'sub', texto: t('Documentos trocados com os clientes') });
      b.push(linhas.length ? { t: 'tabela', colunas: [t('Cliente'), t('Enviados pelo escritório'), t('Recebidos do cliente')], larguras: [0.5, 0.25, 0.25], linhas }
        : { t: 'par', texto: t('Nenhum documento trocado neste mês.') });
    }
    if (tem('pendencias')) {
      const pend = base.map(c => ({ c, s: situacaoPasta(pastaDe(c, portal), portal) })).filter(x => x.s.cor === '🔴');
      b.push({ t: 'sub', texto: t('Clientes com pendência ({n})', { n: pend.length }) });
      b.push(pend.length ? { t: 'tabela', colunas: [t('Cliente'), t('Pendência')], larguras: [0.45, 0.55], linhas: pend.map(x => [String(x.c.c.nome ?? ''), x.s.linha]) }
        : { t: 'par', texto: t('Nenhum cliente com pendência.') });
    }
    return b;
  }

  function doCliente(c: Reg): BlocoPdf[] {
    const tem = (p: ParteC) => selC.includes(p);
    const p = pastaDe(c, portal);
    const docs = p ? portal.filter(r => r.col === 'documento' && r.pai === p.id) : [];
    const peds = p ? portal.filter(r => r.col === 'pedido' && r.pai === p.id) : [];
    const b: BlocoPdf[] = [{ t: 'titulo', texto: t('Relatório do mês · {m}', { m: rotuloMes(mes) }) }, { t: 'par', centro: true, texto: String(c.c.nome ?? '') }];
    if (tem('docs')) {
      const ds = docs.filter(d => String(d.c.competencia ?? '') === comp(mes) || (!d.c.competencia && doMes(d.c.data || d.criado, mes)));
      b.push({ t: 'sub', texto: t('Documentos da competência {m} ({n})', { m: comp(mes), n: ds.length }) });
      if (ds.length) b.push({ t: 'tabela', colunas: [t('Documento'), t('Categoria'), t('Quem enviou'), t('Situação')], larguras: [0.4, 0.22, 0.22, 0.16],
        linhas: ds.map(d => [tituloDoc(d), t(String(d.c.categoria ?? '')), ehDoCliente(d) ? t('do cliente') : t('do escritório'), t(String(d.c.status ?? ''))]) });
    }
    if (tem('pedidos')) {
      const ps = peds.filter(x => doMes(x.c.prazo, mes) || !x.c.entregue);
      b.push({ t: 'sub', texto: t('Pedidos de documentos ({n})', { n: ps.length }) });
      if (ps.length) b.push({ t: 'tabela', colunas: [t('O que precisa'), t('Prazo'), t('Situação')], larguras: [0.56, 0.18, 0.26],
        linhas: ps.map(x => [String(x.c.oque ?? ''), br(x.c.prazo), x.c.entregue ? t('Recebido') : x.c.prazo && x.c.prazo < today() ? t('Atrasado') : t('Pendente')]) });
    }
    if (tem('guias')) {
      const gs = docs.filter(d => d.c.vencimento && doMes(d.c.vencimento, mes));
      const pagas = gs.filter(d => d.c.status === 'Pago'), venc = gs.filter(d => d.c.status !== 'Pago' && d.c.vencimento < today());
      b.push({ t: 'sub', texto: t('Guias do mês: {p} pagas · {v} vencidas', { p: pagas.length, v: venc.length }) });
      if (gs.length) b.push({ t: 'tabela', colunas: [t('Guia'), t('Vencimento'), t('Valor'), t('Situação')], larguras: [0.42, 0.18, 0.2, 0.2],
        linhas: gs.map(d => [tituloDoc(d), br(d.c.vencimento), d.c.valor ? dinheiro(Number(d.c.valor), moeda) : '',
          d.c.status === 'Pago' ? t('Pago') : d.c.vencimento < today() ? t('Vencida') : t('A pagar')]) });
    }
    if (tem('honorarios')) {
      const ps = recebsDe(c.id).flatMap(r => parcelasDe(r)).filter(x => doMes(x.venc, mes) || doMes(x.pagoEm, mes));
      const rec = ps.reduce((s, x) => s + (doMes(x.pagoEm, mes) ? Number(x.recebido) || 0 : 0), 0);
      const aRec = ps.filter(x => !quitada(x) && !atrasada(x)).reduce((s, x) => s + falta(x), 0);
      const atr = ps.filter(x => atrasada(x)).reduce((s, x) => s + falta(x), 0);
      b.push({ t: 'sub', texto: t('Honorários') });
      b.push({ t: 'tabela', colunas: [t('Honorário'), t('Recebido no mês'), t('A receber'), t('Atrasado')], larguras: [0.25, 0.25, 0.25, 0.25],
        linhas: [[c.c.honorario ? dinheiro(Number(c.c.honorario), moeda) : '—', dinheiro(rec, moeda), dinheiro(aRec, moeda), dinheiro(atr, moeda)]] });
    }
    return b;
  }

  const gerar = async () => {
    if (gerando) return;
    setGerando(true);
    try {
      const blocos = cli ? doCliente(cli) : await doEscritorio();
      marcarAbaAtual('escritorio');
      await semBloqueio(() => gerarECompartilharPdf(t('Relatório {m} {q}', { m: comp(mes).replace('/', '-'), q: cli ? String(cli.c.nome ?? '') : g?.nome ?? '' }).trim(), blocos, 'My Planner'));
    } catch (e: any) { Alert.alert(t('Não foi possível gerar o PDF.'), String(e?.message ?? e)); }
    finally { setGerando(false); }
  };
  const Marca = ({ on, label, fn }: { on: boolean; label: string; fn: () => void }) => (
    <Pressable onPress={fn} accessibilityRole="checkbox" accessibilityState={{ checked: on }} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 5 }}>
      <Text style={{ fontSize: 20, color: on ? cor : C.sub }}>{on ? '☑' : '☐'}</Text><Text style={{ color: C.text, flex: 1 }}>{label}</Text>
    </Pressable>);
  return (
    <Sheet visible={visible} onClose={onClose} title={t('Relatório do mês')} larga>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 60 }}>
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('MÊS')}</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          {meses().map(m => <Chip key={m} small cor={cor} on={m === mes} label={comp(m)} onPress={() => setMes(m)} />)}
        </View>
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('DE QUEM')}</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          <Chip small cor={cor} on={!quem} label={'🏢 ' + t('O escritório')} onPress={() => setQuem(null)} />
          {base.map(c => <Chip key={c.id} small cor={cor} on={quem === c.id} label={String(c.c.nome ?? '')} onPress={() => setQuem(c.id)} />)}
        </View>
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('O QUE INCLUIR')}</Text>
        {!cli ? PARTES_ESC.map(([k, n]) => <Marca key={k} on={selE.includes(k)} label={t(n)} fn={() => setSelE(l => (l.includes(k) ? l.filter(x => x !== k) : [...l, k]))} />)
          : PARTES_CLI.map(([k, n]) => <Marca key={k} on={selC.includes(k)} label={t(n)} fn={() => setSelC(l => (l.includes(k) ? l.filter(x => x !== k) : [...l, k]))} />)}
        {!cli && !g && <Text style={{ color: C.mute, fontSize: 11 }}>{t('Reuniões, tarefas e documentos internos entram quando há um escritório (equipe).')}</Text>}
        <Btn cor={cor} outline disabled={gerando || (cli ? !selC.length : !selE.length)} label={'📄 ' + (gerando ? t('Gerando…') : t('Exportar em PDF'))} onPress={gerar} />
        <Text style={{ color: C.mute, fontSize: 11 }}>{t('CPF, CNPJ, anotações internas e conversas nunca vão para o relatório.')}</Text>
        <RelatoriosGerados aba="escritorio" cor={cor} />
      </ScrollView>
    </Sheet>
  );
}
